/**
 * storage.js — remembers a dismissal.
 *
 * State is keyed *per event*, so snoozing Independence Day never suppresses
 * Christmas. Written to a cookie scoped to the widest matching family domain
 * (shared across that domain's subdomains) and mirrored to localStorage, which
 * covers hosts outside FAMILY_DOMAINS and browsers that block cookies.
 */

import { FAMILY_DOMAINS, SNOOZE_PREFIX } from './config.js';

const key = (eventId) => SNOOZE_PREFIX + eventId;

/**
 * Family domain that covers this host, as a cookie `domain` value.
 *
 * The *longest* match wins, not the first, so the result never depends on the
 * order of FAMILY_DOMAINS — which matters for near-neighbours like
 * jobsacademy.co and jobsacademy.co.in.
 *
 * Returns null for IPs, single-label hosts (localhost) and unlisted domains,
 * all of which fall through to localStorage.
 */
export function cookieDomain(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h || h.indexOf('.') === -1) return null;
  if (/^[\d.]+$/.test(h) || h.indexOf(':') !== -1) return null;
  let match = null;
  for (const d of FAMILY_DOMAINS) {
    if (h !== d && !h.endsWith('.' + d)) continue;
    if (!match || d.length > match.length) match = d;
  }
  return match ? '.' + match : null;
}

function readCookie(name) {
  try {
    const needle = name + '=';
    for (const part of String(document.cookie || '').split(';')) {
      const c = part.trim();
      if (c.indexOf(needle) === 0) return decodeURIComponent(c.slice(needle.length));
    }
  } catch (e) { /* cookies unavailable */ }
  return null;
}

function writeCookie(name, value, expiresAt) {
  try {
    const domain = cookieDomain(location.hostname);
    // `Secure` is only legal over https — sending it on http gets the cookie dropped.
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; expires=' + new Date(expiresAt).toUTCString() +
      '; path=/' + (domain ? '; domain=' + domain : '') +
      '; SameSite=Lax' + secure;
    return true;
  } catch (e) {
    return false;
  }
}

function store() {
  try {
    const ls = window.localStorage;
    const probe = SNOOZE_PREFIX + 'probe';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return ls;
  } catch (e) {
    return null; // Safari private mode, disabled storage, sandboxed iframe
  }
}

/** Epoch ms until which this event stays hidden. 0 = not snoozed. */
export function getSnoozeUntil(eventId) {
  const k = key(eventId);
  let raw = readCookie(k);
  if (raw === null) {
    const ls = store();
    if (ls) { try { raw = ls.getItem(k); } catch (e) { /* ignore */ } }
  }
  const ts = parseInt(raw, 10);
  return isFinite(ts) && ts > 0 ? ts : 0;
}

export function setSnooze(eventId, hours) {
  const until = Date.now() + Math.max(0.25, Number(hours) || 2) * 3600000;
  const k = key(eventId);
  // Cookie outlives the snooze by a day so it is not evicted mid-window.
  writeCookie(k, String(until), until + 86400000);
  const ls = store();
  if (ls) { try { ls.setItem(k, String(until)); } catch (e) { /* quota */ } }
  return until;
}

export function clearSnooze(eventId) {
  const k = key(eventId);
  writeCookie(k, '', Date.now() - 86400000);
  const ls = store();
  if (ls) { try { ls.removeItem(k); } catch (e) { /* ignore */ } }
}
